/**
 * project-template-derive.ts — pure functions for building project + tasks from a ProjectTemplate.
 * No side effects; all IDs and "today" are injected so this is fully unit-testable.
 */
import type { Project, ProjectTemplate, Task } from "../types";
import { addDaysIso } from "../lib/date";

/**
 * daysBetweenIso — number of full calendar days from `from` to `to`.
 * Positive when `to` is in the future relative to `from`; negative otherwise.
 * Uses local midnight via new Date(`${iso}T00:00:00`) to match the rest of date.ts.
 */
export function daysBetweenIso(from: string, to: string): number {
  const fromMs = new Date(`${from}T00:00:00`).getTime();
  const toMs = new Date(`${to}T00:00:00`).getTime();
  return Math.round((toMs - fromMs) / 86_400_000);
}

/**
 * captureOffsetDays — converts a task's dueDate to an offsetDays integer relative to todayIso.
 * Past-due tasks clamp to 0 per spec.
 */
export function captureOffsetDays(dueDate: string | undefined, todayIso: string): number {
  if (!dueDate) return 0;
  const diff = daysBetweenIso(todayIso, dueDate);
  return Math.max(0, diff);
}

/**
 * buildProjectFromTemplate — creates project + task drafts from a template.
 *
 * @param template      - the ProjectTemplate to instantiate
 * @param projectId     - pre-generated id for the new project
 * @param taskIds       - pre-generated ids (one per non-empty task entry, positionally matched)
 * @param projectName   - user-supplied name
 * @param startDateIso  - project start date (ISO YYYY-MM-DD)
 *
 * Deadline = startDate + max(offsetDays across tasks, 0) + 7 days.
 */
export function buildProjectFromTemplate(
  template: ProjectTemplate,
  projectId: string,
  taskIds: string[],
  projectName: string,
  startDateIso: string,
): { project: Project; tasks: Task[] } {
  const maxOffset = template.tasks.reduce((m, t) => Math.max(m, t.offsetDays), 0);
  const deadline = addDaysIso(maxOffset + 7, new Date(`${startDateIso}T00:00:00`));
  const now = new Date().toISOString();

  const project: Project = {
    id: projectId,
    name: projectName.trim() || template.name,
    area: template.area,
    status: "active",
    deadline,
    progress: 0,
    nextAction: template.tasks[0]?.title ?? "",
    createdAt: now,
  };

  const tasks: Task[] = template.tasks.map((t, index) => ({
    id: taskIds[index] ?? `task_tpl_${index}`,
    title: t.title,
    dueDate: addDaysIso(t.offsetDays, new Date(`${startDateIso}T00:00:00`)),
    status: "todo",
    priority: t.priority,
    projectId,
    estimateMinutes: t.estimateMinutes,
    subtasks: [],
    createdAt: now,
  }));

  return { project, tasks };
}
